import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  MessageSquare, Search, ChevronLeft, Star, CheckCheck,
  Phone, User, AlertCircle, Loader2, RefreshCw, ThumbsUp,
  MessageCircle, CheckCircle2, XCircle, Hourglass
} from 'lucide-react';
import { api } from '../utils/apiClient';

// ─── Utilidades ───────────────────────────────────────────────
const formatHora = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
};

const formatFecha = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatFechaCorta = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  const hoy = new Date();
  const diff = Math.floor((hoy - d) / 86400000);
  if (diff === 0) return 'Hoy';
  if (diff === 1) return 'Ayer';
  return d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short' });
};

const ESTADO_CONFIG = {
  iniciada:    { label: 'Iniciada',    color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  en_progreso: { label: 'En progreso', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  completada:  { label: 'Completada',  color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  abandonada:  { label: 'Abandonada',  color: 'bg-rose-500/20 text-rose-400 border-rose-500/30' },
};

const EstadoBadge = ({ estado }) => {
  const cfg = ESTADO_CONFIG[estado] || { label: estado, color: 'bg-slate-700 text-slate-300 border-slate-600' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${cfg.color}`}>
      {cfg.label}
    </span>
  );
};

const StarRating = ({ value, max = 5 }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: max }).map((_, i) => (
      <Star
        key={i}
        size={12}
        className={i < value ? 'fill-yellow-400 text-yellow-400' : 'text-slate-600'}
      />
    ))}
  </div>
);

// ─── Burbuja de mensaje ────────────────────────────────────────
const Burbuja = ({ mensaje, prevDireccion }) => {
  const esSalida = mensaje.direccion === 'salida';
  const showAvatar = !esSalida && prevDireccion !== 'entrada';

  return (
    <div className={`flex ${esSalida ? 'justify-end' : 'justify-start'} mb-1`}>
      {/* Avatar cliente (solo primer mensaje consecutivo entrante) */}
      {!esSalida && (
        <div className={`w-7 h-7 rounded-full flex-shrink-0 mr-2 mt-auto flex items-center justify-center bg-slate-700 ${showAvatar ? 'opacity-100' : 'opacity-0'}`}>
          <User size={14} className="text-slate-400" />
        </div>
      )}

      <div className={`max-w-[72%] group relative`}>
        <div
          className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed shadow-md ${
            esSalida
              ? 'bg-[#005c4b] text-white rounded-br-sm'
              : 'bg-slate-700 text-slate-100 rounded-bl-sm'
          }`}
        >
          {/* Indicador de cola */}
          {esSalida && (
            <div className="absolute bottom-0 right-0 translate-x-1 translate-y-0 w-0 h-0 border-l-8 border-l-[#005c4b] border-b-8 border-b-transparent" />
          )}
          {!esSalida && (
            <div className="absolute bottom-0 left-0 -translate-x-1 w-0 h-0 border-r-8 border-r-slate-700 border-b-8 border-b-transparent" />
          )}

          {/* Texto con soporte a *negrita* */}
          <p className="whitespace-pre-wrap break-words">
            {mensaje.contenido.split(/\*([^*]+)\*/g).map((part, i) =>
              i % 2 === 1 ? <strong key={i}>{part}</strong> : part
            )}
          </p>

          <div className={`flex items-center gap-1 mt-1 ${esSalida ? 'justify-end' : 'justify-start'}`}>
            <span className={`text-[10px] ${esSalida ? 'text-[#8eada9]' : 'text-slate-500'}`}>
              {formatHora(mensaje.timestamp)}
            </span>
            {esSalida && <CheckCheck size={12} className="text-[#53bdeb]" />}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Panel de resultado de encuesta ───────────────────────────
const ResultadoEncuesta = ({ sesion }) => {
  if (sesion.estado !== 'completada') return null;
  return (
    <div className="mx-4 my-3 rounded-2xl bg-slate-800/60 border border-slate-700/50 p-4 space-y-3">
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
        <CheckCircle2 size={13} className="text-emerald-400" />
        Resultado de encuesta
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-[10px] text-slate-500 mb-1">Atención</p>
          {sesion.calificacion_atencion ? (
            <StarRating value={sesion.calificacion_atencion} />
          ) : <span className="text-xs text-slate-600">—</span>}
        </div>
        <div>
          <p className="text-[10px] text-slate-500 mb-1">Servicio</p>
          {sesion.calificacion_servicio ? (
            <StarRating value={sesion.calificacion_servicio} />
          ) : <span className="text-xs text-slate-600">—</span>}
        </div>
        <div>
          <p className="text-[10px] text-slate-500 mb-1">¿Recomendaría?</p>
          {sesion.recomendaria === true && <span className="text-xs text-emerald-400 font-medium flex items-center gap-1"><ThumbsUp size={11} /> Sí</span>}
          {sesion.recomendaria === false && <span className="text-xs text-rose-400 font-medium">No</span>}
          {sesion.recomendaria === null && <span className="text-xs text-slate-600">—</span>}
        </div>
        <div>
          <p className="text-[10px] text-slate-500 mb-1">Promedio</p>
          <span className="text-sm font-bold text-white">
            {sesion.calificacion_atencion && sesion.calificacion_servicio
              ? ((sesion.calificacion_atencion + sesion.calificacion_servicio) / 2).toFixed(1)
              : '—'}
          </span>
        </div>
      </div>
      {sesion.comentarios && (
        <div>
          <p className="text-[10px] text-slate-500 mb-1">Comentarios</p>
          <p className="text-xs text-slate-300 italic">"{sesion.comentarios}"</p>
        </div>
      )}
    </div>
  );
};

// ─── Vista de chat individual ──────────────────────────────────
const ChatView = ({ sesion, onBack }) => {
  const [mensajes, setMensajes] = useState([]);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);

  const cargar = useCallback(async () => {
    try {
      const data = await api.get(`/encuesta/conversacion/${sesion.id}`);
      setMensajes(data.mensajes || []);
    } catch {
      setMensajes([]);
    } finally {
      setLoading(false);
    }
  }, [sesion.id]);

  useEffect(() => {
    setLoading(true);
    cargar();
  }, [cargar]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensajes]);

  return (
    <div className="flex flex-col h-full">
      {/* Header del chat */}
      <div className="flex items-center gap-3 px-4 py-3 bg-slate-800/80 border-b border-slate-700/50 flex-shrink-0">
        <button
          onClick={onBack}
          className="md:hidden p-1.5 rounded-full hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
        >
          <ChevronLeft size={20} />
        </button>
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-600 to-teal-700 flex items-center justify-center flex-shrink-0 shadow-md">
          <span className="text-white text-sm font-bold">
            {sesion.cliente.trim().split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-white text-sm truncate">{sesion.cliente}</p>
          <div className="flex items-center gap-2">
            <Phone size={10} className="text-slate-500" />
            <span className="text-[11px] text-slate-400">{sesion.telefono}</span>
            {sesion.expediente && (
              <span className="text-[10px] text-slate-500 font-mono">· {sesion.expediente}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <EstadoBadge estado={sesion.estado} />
          <button
            onClick={cargar}
            className="p-1.5 rounded-full hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
            title="Actualizar"
          >
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* Fondo de chat al estilo WhatsApp */}
      <div
        className="flex-1 overflow-y-auto custom-scrollbar px-4 py-4"
        style={{
          background: 'radial-gradient(ellipse at top, #0d1f1a 0%, #0b1117 100%)',
          backgroundImage: `radial-gradient(ellipse at top, #0d1f1a 0%, #0b1117 100%), url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.012'%3E%3Cpath d='M20 20c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10zm10 0c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10z'/%3E%3C/g%3E%3C/svg%3E")`,
        }}
      >
        {/* Indicador de fecha */}
        <div className="flex justify-center mb-4">
          <span className="bg-slate-800/70 text-slate-400 text-[11px] px-3 py-1 rounded-full">
            {formatFecha(sesion.fecha_inicio)}
          </span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 size={20} className="animate-spin text-slate-500" />
          </div>
        ) : mensajes.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-slate-500">
            <MessageCircle size={32} className="mb-2 opacity-40" />
            <p className="text-sm">Sin mensajes</p>
          </div>
        ) : (
          <>
            {mensajes.map((msg, idx) => (
              <Burbuja
                key={msg.id}
                mensaje={msg}
                prevDireccion={idx > 0 ? mensajes[idx - 1].direccion : null}
              />
            ))}
            {/* Resultado de encuesta si está completada */}
            {sesion.estado === 'completada' && (
              <div className="mt-4">
                <ResultadoEncuesta sesion={sesion} />
              </div>
            )}
          </>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Footer: encuesta finalizada / en curso */}
      <div className="flex-shrink-0 px-4 py-3 bg-slate-900/80 border-t border-slate-700/50">
        {sesion.estado === 'completada' && (
          <div className="flex items-center justify-center gap-2 text-emerald-400 text-xs">
            <CheckCircle2 size={14} />
            <span>Encuesta completada</span>
          </div>
        )}
        {sesion.estado === 'abandonada' && (
          <div className="flex items-center justify-center gap-2 text-rose-400 text-xs">
            <XCircle size={14} />
            <span>Encuesta abandonada por el cliente</span>
          </div>
        )}
        {(sesion.estado === 'iniciada' || sesion.estado === 'en_progreso') && (
          <div className="flex items-center justify-center gap-2 text-amber-400 text-xs">
            <Hourglass size={14} />
            <span>Esperando respuesta del cliente…</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Ítem de lista de conversaciones ──────────────────────────
const ConversacionItem = ({ sesion, activo, onClick }) => {
  const promedio = sesion.calificacion_atencion && sesion.calificacion_servicio
    ? ((sesion.calificacion_atencion + sesion.calificacion_servicio) / 2).toFixed(1)
    : null;

  return (
    <button
      onClick={() => onClick(sesion)}
      className={`w-full text-left px-4 py-3.5 flex items-start gap-3 border-b border-slate-800/60 hover:bg-slate-700/30 transition-colors duration-150 ${
        activo ? 'bg-slate-700/40 border-l-2 border-l-emerald-500' : ''
      }`}
    >
      {/* Avatar */}
      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-emerald-600 to-teal-700 flex items-center justify-center flex-shrink-0 shadow-md">
        <span className="text-white text-sm font-bold">
          {sesion.cliente.trim().split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()}
        </span>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className="font-semibold text-slate-100 text-sm truncate">{sesion.cliente}</p>
          <span className="text-[10px] text-slate-500 flex-shrink-0 mt-0.5">
            {formatFechaCorta(sesion.fecha_inicio)}
          </span>
        </div>

        <div className="flex items-center gap-2 mt-0.5">
          <EstadoBadge estado={sesion.estado} />
          {promedio && (
            <span className="text-[10px] text-yellow-400 flex items-center gap-0.5">
              <Star size={9} className="fill-yellow-400" />
              {promedio}
            </span>
          )}
        </div>

        <div className="flex items-center justify-between mt-1">
          <p className="text-xs text-slate-500 truncate">
            {sesion.expediente ? `Exp. ${sesion.expediente}` : sesion.telefono}
          </p>
          <span className="text-[10px] text-slate-600">{sesion.total_mensajes} msgs</span>
        </div>
      </div>
    </button>
  );
};

// ─── Componente principal ──────────────────────────────────────
const ChatEncuesta = () => {
  const [sesiones, setSesiones] = useState([]);
  const [sesionActiva, setSesionActiva] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [showChat, setShowChat] = useState(false); // control móvil

  const cargarSesiones = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get('/encuesta/conversaciones');
      setSesiones(data || []);
    } catch {
      setError('No se pudieron cargar las conversaciones.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarSesiones();
  }, [cargarSesiones]);

  const sesionesFiltradas = sesiones.filter(s =>
    s.cliente.toLowerCase().includes(busqueda.toLowerCase()) ||
    (s.expediente || '').toLowerCase().includes(busqueda.toLowerCase()) ||
    s.telefono.includes(busqueda)
  );

  const handleSeleccionar = (sesion) => {
    setSesionActiva(sesion);
    setShowChat(true);
  };

  const handleVolver = () => {
    setShowChat(false);
  };

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-slate-950 to-slate-900">
      {/* Header de sección */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-slate-800/60 flex-shrink-0">
        <div>
          <h1 className="text-xl font-extrabold text-white tracking-tight flex items-center gap-2">
            <MessageSquare size={20} className="text-emerald-400" />
            Conversaciones WhatsApp
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">Historial de encuestas de satisfacción</p>
        </div>
        <button
          onClick={cargarSesiones}
          className="p-2 rounded-xl hover:bg-slate-700/50 text-slate-400 hover:text-white transition-colors"
          title="Actualizar lista"
        >
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Layout de dos paneles */}
      <div className="flex flex-1 overflow-hidden">

        {/* Panel izquierdo: lista de conversaciones */}
        <div className={`flex flex-col bg-slate-900/60 border-r border-slate-800/60
          ${showChat ? 'hidden md:flex' : 'flex'}
          w-full md:w-80 lg:w-96 flex-shrink-0`}
        >
          {/* Buscador */}
          <div className="px-3 py-3 border-b border-slate-800/60">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                value={busqueda}
                onChange={e => setBusqueda(e.target.value)}
                placeholder="Buscar cliente, expediente…"
                className="w-full pl-9 pr-3 py-2 rounded-xl bg-slate-800/70 border border-slate-700/50 text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-emerald-500/50"
              />
            </div>
          </div>

          {/* Lista */}
          <div className="flex-1 overflow-y-auto custom-scrollbar">
            {loading ? (
              <div className="flex items-center justify-center h-40 gap-2 text-slate-500">
                <Loader2 size={18} className="animate-spin" />
                <span className="text-sm">Cargando…</span>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center h-40 gap-3 px-4 text-center">
                <AlertCircle size={24} className="text-rose-400" />
                <p className="text-sm text-slate-400">{error}</p>
                <button onClick={cargarSesiones} className="text-xs text-emerald-400 underline">
                  Reintentar
                </button>
              </div>
            ) : sesionesFiltradas.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 gap-2 text-slate-500 px-4 text-center">
                <MessageSquare size={28} className="opacity-30" />
                <p className="text-sm">
                  {busqueda ? 'Sin resultados para la búsqueda' : 'No hay conversaciones aún'}
                </p>
              </div>
            ) : (
              sesionesFiltradas.map(s => (
                <ConversacionItem
                  key={s.id}
                  sesion={s}
                  activo={sesionActiva?.id === s.id}
                  onClick={handleSeleccionar}
                />
              ))
            )}
          </div>

          {/* Pie con total */}
          {sesiones.length > 0 && (
            <div className="px-4 py-2 border-t border-slate-800/60 text-center">
              <span className="text-[10px] text-slate-600">{sesiones.length} conversaciones totales</span>
            </div>
          )}
        </div>

        {/* Panel derecho: chat */}
        <div className={`flex-1 overflow-hidden
          ${showChat ? 'flex' : 'hidden md:flex'}
          flex-col`}
        >
          {sesionActiva ? (
            <ChatView
              key={sesionActiva.id}
              sesion={sesionActiva}
              onBack={handleVolver}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full gap-4 text-slate-600 select-none">
              <div className="w-20 h-20 rounded-full bg-slate-800/50 flex items-center justify-center">
                <MessageSquare size={36} className="opacity-30 text-emerald-400" />
              </div>
              <div className="text-center">
                <p className="text-base font-medium text-slate-500">Selecciona una conversación</p>
                <p className="text-sm text-slate-600 mt-1">para ver el historial del chat</p>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default ChatEncuesta;
